import React, { useState } from "react";
import { CircularProgress, MenuItem, Switch, TextField } from "@mui/material";
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import PopupBase from "../base/PopupBase";
import ConfirmPopup from "../ConfirmPopup/ConfirmPopup";
import CustomButton from "../../components/buttons/CustomButton";
import { DataProviderInstance } from '../../api/DataProvider';
import LeadsConfig from '../../consts/LeadsConfig';
import { isLeadPhoneComplete, onlyDigits } from '../../utils/leadUtils';
import { fullDate } from '../../pages/leads/components/leadVisuals';
import styles from './CreateEditLeadPopup.module.css';

// Traduções amigáveis
const friendlyNames = {
  // Status
  [LeadsConfig.StatusNew]: "Novo",
  [LeadsConfig.StatusInProgress]: "Em andamento",
  [LeadsConfig.StatusWaitingResponse]: "Aguardando retorno",
  [LeadsConfig.StatusFinished]: "Finalizado",
  // Resultados
  [LeadsConfig.ResultPending]: "Pendente",
  [LeadsConfig.ResultSuccess]: "Agendado",
  [LeadsConfig.ResultFailure]: "Não agendado",
  [LeadsConfig.ResultFailureInactive]: "Inativo"
};

/**
 * Criar ou editar um lead. `OnUpdate(message)` recebe o texto do aviso de
 * sucesso (criado, salvo ou excluído); a página recarrega o quadro e mostra o aviso.
 * Lead que ainda não virou paciente pode ser excluído, com confirmação.
 */
function CreateEditLeadPopup(props) {
  const { Data, OnHide, OnUpdate } = props;

  const { CompanyConfig, SetLead, UpdateLead, DeleteLead } = DataProviderInstance();

  const initialLeadData = {
    id: Data?.id || null,
    // Leads criados manualmente nascem válidos; na edição mantém o valor atual
    valid: Data ? (Data.valid || 0) : 1,
    createdAt: Data?.createdAt || "",
    name: Data?.name || "",
    phone: Data?.phone || "",
    channel: Data?.channel || null,
    campaignId: Data?.campaignId || null,
    sourceId: Data ? (CompanyConfig.LeadsCampaigns.find((camp) => camp.id == Data?.campaignId + "")?.sourceId || null) : null,
    status: Data?.status || "new",
    result: Data?.result || "pending",
    observations: Data?.observations || "",
  };

  const [leadData, setLeadData] = useState(initialLeadData);
  const [errors, setErrors] = useState({ name: false, phone: false });
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Só o lead que não virou paciente pode ser excluído (o servidor também recusa)
  const canDelete = !!Data?.id && !Data?.patientId;

  // Na edição o telefone pode ser corrigido; a conversa do WhatsApp fica no número antigo
  const phoneChanged = !!Data && onlyDigits(leadData.phone) !== onlyDigits(Data.phone);

  const statusOptions = LeadsConfig.StatusOptions;
  const resultOptions = LeadsConfig.ResultOptions;
  const channelOptions = LeadsConfig.ChannelOptions;

  // The arrays are now guaranteed to be initialized in DataProvider.js
  const sourceOptions = CompanyConfig.LeadsSources.map((item) => ({
    id: item.id,
    name: item.name,
  }));

  const campaignOptions = CompanyConfig.LeadsCampaigns.map((item) => ({
    id: item.id,
    name: item.name,
    sourceId: item.sourceId,
  }));

  // A campanha selecionada vive pelo id; o nome é apenas o rótulo exibido
  const selectedCampaign = campaignOptions.find((item) => item.id == leadData.campaignId);

  const handleChange = (field, value) => {
    const updatedLeadData = { ...leadData, [field]: value };

    if (field == "campaign") {
      const selectedCampaign = campaignOptions.find((item) => item.id == value);
      updatedLeadData.campaignId = selectedCampaign?.id || "";
      updatedLeadData.sourceId = selectedCampaign?.sourceId;
    }

    if (field == "name" || field == "phone") {
      setErrors((previous) => ({ ...previous, [field]: false }));
    }
    setSaveError("");

    if (field == "result") {
      // Atualiza o status baseado no resultado selecionado
      switch(value) {
        case LeadsConfig.ResultPending:
          updatedLeadData.status = LeadsConfig.StatusInProgress;
          break;
        case LeadsConfig.ResultSuccess:
        case LeadsConfig.ResultFailure:
        case LeadsConfig.ResultFailureInactive:
          updatedLeadData.status = LeadsConfig.StatusFinished;
          break;
        default:
          break;
      }
    }

    if (field == "status") {
      // Atualiza o resultado baseado no status selecionado
      switch(value) {
        case LeadsConfig.StatusNew:
        case LeadsConfig.StatusInProgress:
        case LeadsConfig.StatusWaitingResponse:
          updatedLeadData.result = LeadsConfig.ResultPending;
          break;
        case LeadsConfig.StatusFinished:
          if (updatedLeadData.result == LeadsConfig.ResultPending) {
            updatedLeadData.result = LeadsConfig.ResultSuccess;
          }
          break;
        default:
          break;
      }
    }

    setLeadData(updatedLeadData);
  };

  // Só fecha quando o servidor confirma; senão o erro aparece no popup
  const onSaveResponse = (response) => {
    setSaving(false);
    if (response?.status == "success") {
      OnUpdate(Data ? "Lead salvo" : "Lead criado");
      OnHide();
      return;
    }
    setSaveError(response?.message || response?.error || "Não foi possível salvar o lead. Tente novamente.");
  };

  const createLead = () => {
    setSaving(true);
    SetLead(leadData, onSaveResponse);
  };

  const updateData = () => {
    setSaving(true);
    UpdateLead(leadData, onSaveResponse);
  };

  const deleteLead = () => {
    setSaving(true);
    DeleteLead(Data.id, (response) => {
      setSaving(false);
      if (response?.status == "success") {
        OnUpdate("Lead excluído");
        OnHide();
        return;
      }
      setSaveError(response?.message || "Não foi possível excluir o lead. Tente novamente.");
    });
  };

  const validateForm = () => {
    const nameError = !String(leadData.name || "").trim();
    const phoneError = !isLeadPhoneComplete(leadData.phone);

    setErrors({ name: nameError, phone: phoneError });

    return !nameError && !phoneError;
  };

  const saveClickHandler = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (saving || !validateForm()) return;

    if (Data) {
      updateData();
    } else {
      createLead();
    }
  };

  // Origem é só leitura: vem da campanha escolhida
  const sourceName = leadData.sourceId
    ? (sourceOptions.find((item) => item.id == leadData.sourceId + "")?.name || "Desconhecida")
    : "";
  const sourceHint = sourceOptions.length === 0
    ? "Nenhuma origem cadastrada ainda. Cadastre em Marketing > Origens e ligue a uma campanha."
    : "Preenchida pela campanha escolhida.";

  const createdAtLabel = Data?.createdAt ? fullDate(Data.createdAt) : "";

  // Select sem rótulo interno: o rótulo fica acima do campo. Valor nunca null (aviso do MUI).
  const renderSelect = (id, value, onChange, options, placeholder) => (
    <TextField
      id={id}
      select
      value={value ?? ""}
      onChange={onChange}
      disabled={saving}
      fullWidth
      size="small"
      className={styles.input}
      SelectProps={{ displayEmpty: true }}
    >
      {placeholder && <MenuItem value="" disabled>{placeholder}</MenuItem>}
      {options}
    </TextField>
  );

  const content = (
    <form className={styles.content} onSubmit={saveClickHandler} noValidate>
      {saveError && (
        <div className={styles.error} role="alert">{saveError}</div>
      )}

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Contato</h4>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="lead-name">Nome *</label>
          <TextField
            id="lead-name"
            value={leadData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={saving}
            fullWidth
            size="small"
            autoFocus={!Data}
            error={errors.name}
            helperText={errors.name ? "Preencha o nome do lead" : ""}
            className={styles.input}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="lead-phone">Telefone *</label>
          <TextField
            id="lead-phone"
            placeholder="DDD + número"
            value={leadData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            disabled={saving}
            fullWidth
            size="small"
            error={errors.phone}
            helperText={errors.phone
              ? "Informe o telefone completo, com DDD e número"
              : (phoneChanged ? "A conversa de WhatsApp continua no número antigo." : "")}
            className={styles.input}
          />
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Como chegou</h4>
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="lead-channel">Canal</label>
            {renderSelect("lead-channel", leadData.channel, (e) => handleChange("channel", e.target.value),
              channelOptions.map((channel) => (
                <MenuItem key={channel} value={channel}>{LeadsConfig.GetChannelFriendlyName(channel)}</MenuItem>
              )), "Selecione")}
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="lead-campaign">Campanha</label>
            {renderSelect("lead-campaign", selectedCampaign?.id, (e) => handleChange("campaign", e.target.value),
              campaignOptions.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id}>{campaign.name}</MenuItem>
              )), campaignOptions.length ? "Selecione" : "Nenhuma campanha")}
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="lead-source">Origem</label>
          <TextField
            id="lead-source"
            value={sourceName}
            placeholder="Sem origem"
            disabled
            fullWidth
            size="small"
            helperText={sourceHint}
            className={styles.input}
          />
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Situação</h4>
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="lead-status">Etapa</label>
            {renderSelect("lead-status", leadData.status, (e) => handleChange("status", e.target.value),
              statusOptions.map((status) => (
                <MenuItem key={status} value={status}>{friendlyNames[status]}</MenuItem>
              )))}
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="lead-result">Resultado</label>
            {renderSelect("lead-result", leadData.result, (e) => handleChange("result", e.target.value),
              resultOptions.map((result) => (
                <MenuItem key={result} value={result}>{friendlyNames[result]}</MenuItem>
              )))}
          </div>
        </div>
        <label className={styles.toggle}>
          <span className={styles.toggleIcon}><ToggleOnOutlinedIcon fontSize="small" /></span>
          <span className={styles.toggleText}>
            <span className={styles.toggleTitle}>Lead válido</span>
            <span className={styles.hint}>Desligue para tirar o lead do funil sem excluir.</span>
          </span>
          <Switch
            checked={leadData.valid == "1"}
            onChange={(e) => handleChange("valid", e.target.checked ? 1 : 0)}
            disabled={saving}
            inputProps={{ 'aria-label': 'Lead válido' }}
          />
        </label>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="lead-observations">Observações</label>
          <TextField
            id="lead-observations"
            value={leadData.observations}
            onChange={(e) => handleChange("observations", e.target.value)}
            disabled={saving}
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Opcional"
            className={styles.input}
          />
        </div>
        {createdAtLabel && <span className={styles.hint}>Chegou em {createdAtLabel}</span>}
      </section>

      {/* Enter num campo de uma linha envia o formulário */}
      <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
    </form>
  );

  const footer = (
    <div className={styles.footer}>
      {canDelete && (
        <CustomButton style="error" variant="text" label="Excluir" onClick={() => setConfirmDelete(true)} enabled={!saving} />
      )}
      <span className={styles.footerSpacer} />
      <CustomButton style="primary" variant="text" label="Cancelar" onClick={OnHide} enabled={!saving} />
      <CustomButton
        style="main"
        variant="contained"
        label={Data ? "Salvar" : "Criar lead"}
        icon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        onClick={saveClickHandler}
        enabled={!saving}
      />
    </div>
  );

  return (
    <>
      <PopupBase
        Title={Data ? "Editar lead" : "Novo lead"}
        Content={content}
        Footer={footer}
        OnClose={OnHide}
        maxWidth="sm"
      />
      <ConfirmPopup
        Visibility={confirmDelete}
        Title="Excluir lead"
        Description={`O lead "${Data?.name || Data?.phone || ""}" sai do quadro e não dá para recuperar. Para só tirar do funil, desligue "Lead válido".`}
        ConfirmLabel="Excluir"
        OnConfirm={deleteLead}
        OnHide={() => setConfirmDelete(false)}
      />
    </>
  );
}

export default CreateEditLeadPopup;
